'use client';

import useSWRInfinite from 'swr/infinite';
import { refundsApi } from '@/lib/api';
import type { RefundCandidatesResponse } from '@/lib/api';
import { TRANSACTION_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

const PAGE_SIZE = 20;

/**
 * Candidates for linking a refund to `transactionId` (the anchor may be either
 * side), paged by offset — `setSize(size + 1)` loads the next window while the
 * last page reports `hasMore`. `q` must arrive already debounced — the picker
 * drawer owns the 250ms debounce (and resets paging when the term settles), so
 * the key sequence only changes when the inputs do.
 */
export function useRefundCandidates(
  transactionId: string | null,
  q: string,
  excludeLinkId?: string | null,
) {
  const authedFetch = useAuthedFetcher();

  return useSWRInfinite<RefundCandidatesResponse>(
    (pageIndex, previousPage) => {
      if (!transactionId) return null;
      if (previousPage && !previousPage.hasMore) return null;
      const offset = pageIndex === 0 || !previousPage
        ? 0
        : previousPage.offset + previousPage.results.length;
      return ['refund-candidates', transactionId, q, offset, excludeLinkId ?? ''] as const;
    },
    async (key) => {
      const offset = Number(key[3] ?? 0);
      return authedFetch((token) =>
        refundsApi.getCandidates(token, transactionId!, {
          q: q || undefined,
          limit: PAGE_SIZE,
          offset,
          excludeLinkId: excludeLinkId || undefined,
        }),
      );
    },
    { ...TRANSACTION_SWR_OPTIONS, revalidateFirstPage: false },
  );
}
